import { metrics } from './metrics';
import { logger } from './logger';

interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoff?: number;
  timeout?: number;
}

const defaultOptions: RetryOptions = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoff: 2,
  timeout: 30000,
};

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const startTime = Date.now();
  const opts = { ...defaultOptions, ...options };
  let attempt = 1;

  const retryOperation = async (): Promise<T> => {
    try {
      const result = await operation();
      
      // Registrar métricas de sucesso
      const duration = Date.now() - startTime;
      metrics.recordProcessingTime('retry_success', duration);
      metrics.record('retry_attempts', attempt);
      
      return result;
    } catch (error) {
      // Registrar erro e tentativa
      metrics.recordError('retry_attempt_failed');
      logger.warn(`Tentativa ${attempt} falhou`, {
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        attempt,
        maxRetries: opts.maxRetries
      });

      // Se for um erro de rede, tentar novamente
      const isNetworkError = error instanceof Error && 
        (error.message.includes('Failed to fetch') || 
         error.message.includes('NetworkError'));

      if (attempt >= (opts.maxRetries || defaultOptions.maxRetries!) || !isNetworkError) {
        metrics.recordError('retry_max_attempts_reached');
        logger.error('Número máximo de tentativas atingido ou erro não recuperável', {
          attempts: attempt,
          duration: Date.now() - startTime,
          isNetworkError
        });
        throw error;
      }

      // Calcular delay com backoff exponencial
      const delay = Math.min(
        opts.initialDelay! * Math.pow(opts.backoff!, attempt - 1),
        opts.maxDelay!
      );

      attempt++;
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation();
    }
  };

  return Promise.race([
    retryOperation(),
    new Promise<never>((_, reject) => {
      setTimeout(() => {
        metrics.recordError('retry_timeout');
        reject(new Error(`Operação excedeu o timeout de ${opts.timeout}ms`));
      }, opts.timeout);
    })
  ]);
}